(() => {
  const SELECTORS = {
    shell: '[data-intact-search-shell]',
    open: '[data-intact-search-open]',
    close: '[data-intact-search-close]',
    input: '[data-intact-search-input]',
    clear: '[data-intact-search-clear]',
    results: '[data-intact-search-results]',
    cart: '[data-intact-cart-open]',
    headerRoot: '[id^="CHH-"]',
    headerMain: '.chh-main'
  };

  const state = {
    controller: null,
    timer: null,
    lastTrigger: null
  };

  const rootStyle = document.documentElement.style;

  function getShell() {
    return document.querySelector(SELECTORS.shell);
  }

  function updateShellTop() {
    const headerRoot = document.querySelector(SELECTORS.headerRoot);
    const header = headerRoot?.querySelector(SELECTORS.headerMain);
    if (!header) return;

    const rect = header.getBoundingClientRect();
    const top = Math.max(0, Math.round(rect.bottom));
    rootStyle.setProperty('--intact-drawer-top', `${top}px`);
  }

  function closeMobileMenu() {
    const headerRoot = document.querySelector(SELECTORS.headerRoot);
    const drawer = headerRoot?.querySelector('[data-chh-drawer]');
    const overlay = headerRoot?.querySelector('[data-chh-overlay]');
    const openButton = headerRoot?.querySelector('[data-chh-open]');

    if (drawer?.classList.contains('is-open')) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay?.classList.remove('is-open');
      if (openButton) {
        openButton.setAttribute('aria-expanded', 'false');
        openButton.setAttribute('aria-label', 'Open menu');
      }
    }
  }

  function openSearch(trigger) {
    const shell = getShell();
    if (!shell) return;

    if (shell.classList.contains('is-open')) {
      closeSearch();
      return;
    }

    const cart = document.querySelector('cart-drawer');
    if (cart?.classList.contains('active') && typeof cart.close === 'function') {
      cart.close();
    }

    closeMobileMenu();
    updateShellTop();

    state.lastTrigger = trigger || document.activeElement;
    shell.classList.add('is-open');
    shell.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('iw-drawer-open');
    document.body.classList.add('iw-drawer-open');

    /* Keep the search field unfocused when the drawer first opens.
       The pink focus ring will appear only after the user clicks/taps
       inside the input field. */
  }

  function closeSearch({ restoreFocus = true } = {}) {
    const shell = getShell();
    if (!shell) return;

    shell.classList.remove('is-open');
    shell.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('iw-drawer-open');
    document.body.classList.remove('iw-drawer-open');

    state.controller?.abort();
    state.controller = null;
    clearTimeout(state.timer);

    if (restoreFocus && state.lastTrigger instanceof HTMLElement) {
      state.lastTrigger.focus({ preventScroll: true });
    }
  }

  async function runPredictiveSearch(query) {
    const shell = getShell();
    const results = shell?.querySelector(SELECTORS.results);
    if (!results) return;

    const term = query.trim();
    if (!term) {
      results.innerHTML = '';
      results.setAttribute('aria-busy', 'false');
      return;
    }

    state.controller?.abort();
    state.controller = new AbortController();
    results.setAttribute('aria-busy', 'true');

    const url = new URL(window.routes?.predictive_search_url || '/search/suggest', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'intact-predictive-search');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title');

    try {
      const response = await fetch(url.toString(), {
        signal: state.controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section =
        doc.querySelector('#shopify-section-intact-predictive-search') ||
        doc.querySelector('.iw-predictive-fragment');
      results.innerHTML = section ? section.innerHTML : '';
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Intact Search] Predictive search error:', error);
        results.innerHTML = '';
      }
    } finally {
      results.setAttribute('aria-busy', 'false');
    }
  }

  async function refreshHeaderCartCount() {
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('.chh-cart-count').forEach((node) => {
        node.textContent = cart.item_count;
        node.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (_) {}
  }

  function openCart(trigger) {
    closeSearch({ restoreFocus: false });
    closeMobileMenu();
    updateShellTop();

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      if (drawer.classList.contains('active') && typeof drawer.close === 'function') {
        drawer.close();
        return true;
      }
      drawer.open(trigger);
      refreshHeaderCartCount();
      return true;
    }
    return false;
  }

  document.addEventListener('click', (event) => {
    const searchOpen = event.target.closest(SELECTORS.open);
    if (searchOpen) {
      event.preventDefault();
      openSearch(searchOpen);
      return;
    }

    const searchClose = event.target.closest(SELECTORS.close);
    if (searchClose) {
      event.preventDefault();
      closeSearch();
      return;
    }

    const clear = event.target.closest(SELECTORS.clear);
    if (clear) {
      event.preventDefault();
      const shell = getShell();
      const input = shell?.querySelector(SELECTORS.input);
      const results = shell?.querySelector(SELECTORS.results);
      if (input) {
        input.value = '';
        input.focus();
      }
      clear.hidden = true;
      if (results) results.innerHTML = '';
      return;
    }

    const cartOpen = event.target.closest(SELECTORS.cart);
    if (cartOpen && openCart(cartOpen)) {
      event.preventDefault();
    }
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches(SELECTORS.input)) return;

    const shell = getShell();
    const clear = shell?.querySelector(SELECTORS.clear);
    if (clear) clear.hidden = event.target.value.length === 0;

    clearTimeout(state.timer);
    state.timer = setTimeout(() => runPredictiveSearch(event.target.value), 180);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const shell = getShell();
    if (shell?.classList.contains('is-open')) {
      event.preventDefault();
      closeSearch();
    }
  });

  window.addEventListener('resize', updateShellTop, { passive: true });
  window.addEventListener('scroll', updateShellTop, { passive: true });
  window.addEventListener('orientationchange', () => setTimeout(updateShellTop, 80), { passive: true });

  document.addEventListener('DOMContentLoaded', () => {
    updateShellTop();
    refreshHeaderCartCount();
  });
})();